import {
  Box,
  Button,
  Checkbox,
  DialogTitle,
  FormControl,
  InputLabel,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Tooltip,
  Stack,
  Container,
  InputAdornment,
  IconButton,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import EPortAvatar from "../../EPortAvatar";
import Translate from "../../../utils/Translate";
import * as Yup from "yup";
import debounce from "lodash/debounce";
import YesOrNoDialog from "../../YesOrNoDialog";
import ActorAvatar from "../../ActorAvatar";
import { useSnackbar } from "notistack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { actorSearch, actorCompanyTypes } from "../../../utils/actorSearch";
import { accessKeys, hasAccess, hasSomeAccess } from "../../../utils/userAccess";
import { validateVatNumber } from "../../../utils/vatNumber";
import { Clear } from "@mui/icons-material";
import { validatePhoneNumber } from "../../../utils/phoneNumber";
import { nameValidationRegex } from "../../../utils/nameValidation";
import Organisationsnummer from "organisationsnummer";

// validera bara organisationsnummer om detta är en svensk licens, annars validera tecken
const orgNoInvalid = (orgNo) => orgNo 
  && (
      (hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem) && !Organisationsnummer.valid(orgNo)) 
      || (!hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem) && !nameValidationRegex.test(orgNo))
  );

const orgNoInvalidHelperText = () => 
    hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)
      ? Translate.get("InvalidOrganisationNumber")
      : Translate.get("InvalidCharacters");

async function updateAddressName(address, newName, api) {
  let request = {
    actorAddressId: address.actorAddressId,
    actorId: address.actorId,
    addressStreet1: newName,
    addressStreet2: address.actorAddressStreet2,
    zipCode: address.actorAddressZipCode,
    city: address.actorAddressCity,
    country: address.actorAddressCountry,
    addressType: address.actorAddressType,
  };

  try {
    return await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/address`, request, "PUT");
  } catch (e) {
    return;
  }
}

function isSameAddress(address1, address2) {
  return (
    address1.actorAddressStreet1 === address2.actorAddressStreet1 &&
    address1.actorAddressStreet2 === address2.actorAddressStreet2 &&
    address1.actorAddressZipCode === address2.actorAddressZipCode &&
    address1.actorAddressCity === address2.actorAddressCity &&
    address1.actorAddressCountry === address2.actorAddressCountry
  );
}

function isEmptyAddress(address) {
  if (!address) return true;

  for (const property in address) {
    const isRelevantProperty =
      property !== "actorId" &&
      property !== "actorAddressId" &&
      property !== "actorAddressType" &&
      property !== "actorAddressStreet1";
    if (isRelevantProperty && address[property]) {
      return false;
    }
  }
  return true;
}

function isNullUndefinedOrEmpty(str) {
  return str === undefined || str === null || str === "";
}

function getAddressesWithDifferentNames(addresses, actorName) {
  return addresses && addresses.length > 0
    ? addresses
        .filter(
          (adr) =>
            !isNullUndefinedOrEmpty(adr.actorAddressStreet1) && adr.actorAddressStreet1.trim() !== actorName?.trim(),
        )
        .map(
          (adr) =>
            `${adr.actorAddressStreet1}\n${adr.actorAddressStreet2}\n${adr.actorAddressZipCode} ${adr.actorAddressCity}`,
        )
        .join("\n\n")
    : "";
}

async function updateDefaultSeparateInvoiceRefActorId(companyActorId, contactActorId, api) {
  try {
    const request = {
      settingName: "defaultSeparateInvoiceRefActorId",
      settingValue: contactActorId ? contactActorId.toString() : null,
    };
    return await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${companyActorId}/settings`, request, "POST");
  } catch (e) {
    return;
  }
}

// Get the text for submit button when saving an existing actor
function getUpdateActorSubmitText(isEditOnly, hasSwitchedActor, actorId) {
  // isEditOnly determines whether we're only editing an actor or also selecting an actor for
  // a purpose. For example, consider the difference between simply editing a company via the register,
  // and editing the company when selecting it for a specific course. In the latter case we want a change
  // of actor in this dialog to change the company for the course too, and make that effect clear to the user.
  if (isEditOnly || (actorId > 0 && !hasSwitchedActor)) {
    return Translate.get("Save");
  } else {
    return Translate.get("ChooseCompany");
  }
}

const CompanyCrud = ({
  actorId,
  name,
  isEditOnly,
  isMatchingActorsDisabled,
  onActorSaved,
  onClose,
  onCancel,
  hideHeader,
}) => {
  const [matchingActor, setMatchingActor] = useState(null);
  const [submitText, setSubmitText] = useState("OK");
  const [headerText, setHeaderText] = useState(Translate.get("EditCompany"));
  const [formData, setFormData] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [errors, setErrors] = useState({});
  const [isBlankForm, setIsBlankForm] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [imageResponse, setImageResponse] = useState(null);
  const [isPostalSameAsInvoiceAddressChecked, setIsPostalSameAsInvoiceAddressChecked] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [paymentAgreements, setPaymenAgreements] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [allAddresses, setAllAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [defaultSeparateInvoiceRefActorId, setDefaultSeparateInvoiceRefActorId] = useState(null);
  const [showUpdateAddressNameQuestionOnSubmit, setShowUpdateAddressNameQuestionOnSubmit] = useState(false);
  const [showUpdateAddressNameQuestion, setShowUpdateAddressNameQuestion] = useState(false);
  const [hasExtendedAddressManagement] = useState(hasAccess(accessKeys.licenseIssuer));
  const languageOptions = [
    { value: 29, label: "LanguageCodeEnum_Swedish" },
    { value: 9, label: "LanguageCodeEnum_English" },
    { value: 12, label: "LanguageCodeEnum_French" },
  ];

  const api = useApi();

  const location = useLocation();
  const navigate = useNavigate();
  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const schema = Yup.object({
    required: Yup.string().min(1, Translate.get("ePortNameRequired")).optional(),
    validChars: Yup.string().matches(nameValidationRegex, Translate.get("InvalidCharacters")),
    email: Yup.string().email(Translate.get("InvalidEmail")).optional(),
    emailRequired: Yup.string().min(1, "Required - No translation").email(Translate.get("InvalidEmail")).optional(),
    phoneNumber: Yup.string()
      .test("phoneNumber", Translate.get("InvalidPhoneNumber"), function (item) {        
        return !item || validatePhoneNumber(item, true);
      })
      .optional(),
    organisationNumber: Yup.string()
      .test("organisationNumber",
        orgNoInvalidHelperText(),
        function (item) {
          return !orgNoInvalid(item);
        })
        .optional()
  });

  const actorNameAutocompleteSearch = useCallback(async (searchText) => {
    var foundActors = await actorSearch(searchText, actorCompanyTypes, ["ActorName"], 50);
    if (Array.isArray(foundActors)) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
      }));
    }

    // Något gick fel
    return [];
  }, []);

  const debouncedExactMatchActorSearch = useMemo(
    () =>
      debounce(async (searchText, searchFields) => {
        const isValidSearchText = !!searchText && typeof searchText === "string" && searchText.trim() !== "";
        if (!isMatchingActorsDisabled && isValidSearchText) {
          var result = await actorSearch(searchText, actorCompanyTypes, searchFields, 1, true);
          if (!!result && result.length > 0) {
            setMatchingActor(result[0]);
          }
        }
      }, 700),
    [isMatchingActorsDisabled],
  );

  async function loadPaymentAgreements() {
    if (!hasAccess(accessKeys.isMASystem)) {
      return;
    }

    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/paymentagreements`);
    const defaultPaymentAgreement = data.paymentAgreements.find((x) => !!x.isDefaultValue);
    const paymentAgreements = defaultPaymentAgreement
      ? [
          {
            ePortCodeId: "defaultPaymentAgreement",
            description: `Standard (${defaultPaymentAgreement.description})`,
          },
          ...data.paymentAgreements,
        ]
      : data.paymentAgreements;
    setPaymenAgreements(paymentAgreements);
  }

  async function loadCurrencies() {
    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/currencies`);
    setCurrencies(data);
  }

  const loadContacts = useCallback(
    async (actorId) => {
      if (!actorId) {
        return;
      }
      const loadedContacts = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}actors/clients/${actorId}/contacts`,
        false,
        "GET",
        (response) => response && Array.isArray(response),
      );
      if (loadedContacts && Array.isArray(loadedContacts)) {
        setContacts(loadedContacts);
      }
    },
    [api],
  );

  const loadActor = (id) => {
    api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${id}/as-client`, false, "GET").then((data) => {
      if (data) {
        const existingActorFormData = {
          actorId: data.actorId,
          actorName: data.actorName,
          externalActorNo: data.externalActorNo,
          orgNo: data.orgNo,
          actorEmail: data.actorEmail,
          webSite: data.webSite,
          profilePictureUrl: data.profilePictureUrl,
          observationNote: data.observationNote,
          defaultCustomerOrderNo: data.defaultCustomerOrderNo,
          defaultCustomerOrderNoRequired: data.defaultCustomerOrderNoRequired,
          paymentAgreement: data.paymentAgreement ?? "defaultPaymentAgreement",
          defaultCurrency: data.defaultCurrency,
          languageCode: data.languageCode,
          vatType: data.vatType,
          vatNr: data.vatNr,
          isInactive: data.isInactive,
          isAutoOrderAllowed: data.isAutoOrderAllowed,
          businessPhone: {
            actorId: data.actorId,
            actorPhoneNumber: data.businessPhone ? data.businessPhone.actorPhoneNumber : null,
          },
          mobilePhone: {
            actorId: data.actorId,
            actorPhoneNumber: data.mobilePhone ? data.mobilePhone.actorPhoneNumber : null,
          },
          invoiceAddress: {
            actorId: data.actorId,
            actorAddressId: data.invoiceAddress?.actorAddressId,
            actorAddressStreet1: !isNullUndefinedOrEmpty(data.invoiceAddress?.actorAddressStreet1)
              ? data.invoiceAddress.actorAddressStreet1
              : data.actorName,
            actorAddressStreet2: data.invoiceAddress?.actorAddressStreet2,
            actorAddressCity: data.invoiceAddress?.actorAddressCity,
            actorAddressZipCode: data.invoiceAddress?.actorAddressZipCode,
            actorAddressCountry: data.invoiceAddress?.actorAddressCountry,
          },
          postalAddress: {
            actorId: data.actorId,
            actorAddressId: data.postalAddress?.actorAddressId,
            actorAddressStreet1: !isNullUndefinedOrEmpty(data.postalAddress?.actorAddressStreet1)
              ? data.postalAddress.actorAddressStreet1
              : data.actorName,
            actorAddressStreet2: data.postalAddress?.actorAddressStreet2,
            actorAddressCity: data.postalAddress?.actorAddressCity,
            actorAddressZipCode: data.postalAddress?.actorAddressZipCode,
            actorAddressCountry: data.postalAddress?.actorAddressCountry,
          },
        };

        setIsPostalSameAsInvoiceAddressChecked(
          !isEmptyAddress(existingActorFormData.invoiceAddress) &&
            isSameAddress(existingActorFormData.invoiceAddress, existingActorFormData.postalAddress),
        );
        setIsBlankForm(false);
        setFormData(existingActorFormData);
        setAllAddresses(data.allAddresses ?? []);
        setDataFetched(true);
        loadPaymentAgreements();
        loadCurrencies();
        loadContacts(id);
        setDefaultSeparateInvoiceRefActorId(data.defaultSeparateInvoiceRefActorId);
      }
    });
  };

  const uploadImage = (actorId, imageId) => {
    return api.fetch(`${import.meta.env.VITE_IMAGE_SERVICE_URL}UploadActorImageDatabase`, {
      actorId: actorId,
      imageId: imageId,
    });
  };

  async function handleSubmitClick(formData) {
    if (showUpdateAddressNameQuestionOnSubmit) {
      setShowUpdateAddressNameQuestion(true);
    } else {
      // If the user doesn't have extended address management, then we will automatically update address
      // names too, since they have no means to keep address names in sync with company name otherwise.
      const updateAddressNames = !hasExtendedAddressManagement;
      await submitFormData(formData, updateAddressNames);
    }
  }

  async function submitFormData(formData, updateAddressNames) {
    if (isMatchingActorsDisabled && actorId > 0 && formData.actorId !== actorId) {
      // Just a sanity check, to avoid mistakes when making future changes
      throw new Error("Invalid actorId");
    }

    if (formData.invoiceAddress && isNullUndefinedOrEmpty(formData.invoiceAddress?.actorAddressStreet1)) {
      formData.invoiceAddress.actorAddressStreet1 = formData.actorName;
    }

    if (formData.postalAddress && isNullUndefinedOrEmpty(formData.postalAddress?.actorAddressStreet1)) {
      formData.postalAddress.actorAddressStreet1 = formData.actorName;
    }

    setIsSubmitLoading(true);
    delete formData.profilePictureUrl;
    formData.paymentAgreement =
      formData.paymentAgreement === "defaultPaymentAgreement" ? null : formData.paymentAgreement;
    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/clients`, formData);

    if (data && data.isSuccessful) {
      if (imageResponse?.imageId) {
        await uploadImage(data.actorId, imageResponse.imageId);
      }

      let addressesToUpdate = new Array();
      if (data.invoiceAddress != null) {
        addressesToUpdate.push(data.invoiceAddress);
      }
      if (data.postalAddress != null) {
        addressesToUpdate.push(data.postalAddress);
      }

      if (updateAddressNames) {
        for (let adr of addressesToUpdate) {
          await updateAddressName(adr, formData.actorName, api);
        }
      }

      updateDefaultSeparateInvoiceRefActorId(data.actorId, defaultSeparateInvoiceRefActorId, api);

      if (onActorSaved) {
        await onActorSaved({
          actorId: data.actorId,
          actorName: data.actorName,
        });
      }

      if (onClose) {
        onClose();
      } else {
        closeAndReload();
      }
    }
  }

  useEffect(() => {
    const isValueInvalid = (value, validator = "required") => {
      try {
        return schema.validateSync({ [validator]: value ?? "" }) && false;
      } catch (err) {
        return err.errors[0];
      }
    };

    const checkInput = (input, value, validator) => {
      const validationError = isValueInvalid(value, validator);
      setErrors((oldValue) => ({
        ...oldValue,
        [input]: validationError,
      }));

      return validationError;
    };

    const checkCountryRequired = (input, actorAddress) => {
      setErrors((oldValue) => ({
        ...oldValue,
        [input]:
          countryIsRequired(actorAddress) && !actorAddress?.actorAddressCountry?.trim()
            ? Translate.get("Compulsory")
            : false,
      }));
    };

    if (!isBlankForm) {
      checkInput("actorEmail", formData.actorEmail, "email");
      const error = checkInput("actorName", formData.actorName, "required");
      if(!error) {
        checkInput("actorName", formData.actorName, "validChars");
      }
      checkInput("orgNo", formData.orgNo, "organisationNumber");
      checkInput("webpage", formData.webSite, "validChars");
      checkInput("actorPhoneBusiness", formData.businessPhone?.actorPhoneNumber, "phoneNumber");
      checkInput("actorPhoneMobile", formData.mobilePhone?.actorPhoneNumber, "phoneNumber");
      checkInput("invoiceAddressStreet2", formData.invoiceAddress?.actorAddressStreet2, "validChars");
      checkInput("invoiceAddressZipCode", formData.invoiceAddress?.actorAddressZipCode, "validChars");
      checkInput("invoiceAddressCity", formData.invoiceAddress?.actorAddressCity, "validChars");
      checkInput("invoiceAddressCountry", formData.invoiceAddress?.actorAddressCountry, "validChars");
      checkInput("postalAddressStreet2", formData.postalAddress?.actorAddressStreet2, "validChars");
      checkInput("postalAddressZipCode", formData.postalAddress?.actorAddressZipCode, "validChars");
      checkInput("postalAddressCity", formData.postalAddress?.actorAddressCity, "validChars");
      checkInput("postalAddressCountry", formData.postalAddress?.actorAddressCountry, "validChars");

      checkCountryRequired("invoiceAddressCountry", formData.invoiceAddress);
      checkCountryRequired("postalAddressCountry", formData.postalAddress);
    } else {
      setSubmitDisabled(true);
    }
  }, [formData, isBlankForm]);

  useEffect(() => setSubmitDisabled(Object.values(errors).some((x) => x)), [errors]);

  useEffect(() => {
    if (actorId) {
      loadActor(actorId);
      setHeaderText(Translate.get("EditCompany"));
      setSubmitText(getUpdateActorSubmitText(isEditOnly, false, actorId));
    } else {
      setHeaderText(Translate.get("AddCompany"));
      setSubmitText(Translate.get("AddCompany"));
      const blankFormData = {
        actorName: name ?? null,
        externalActorNo: null,
        orgNo: null,
        actorEmail: null,
        businessPhone: { actorPhoneNumber: null },
        mobilePhone: { actorPhoneNumber: null },
        vatType: 1,
        paymentAgreement: "defaultPaymentAgreement",
        invoiceAddress: {
          actorAddressStreet2: null,
          actorAddressCity: null,
          actorAddressZipCode: null,
          actorAddressCountry: null,
        },
        postalAddress: {
          actorAddressStreet2: null,
          actorAddressCity: null,
          actorAddressZipCode: null,
          actorAddressCountry: null,
        },
      };
      setIsBlankForm(!name);
      setFormData(blankFormData);
      setDataFetched(true);
      loadPaymentAgreements();
      loadCurrencies();
    }
  }, [actorId, name, isEditOnly]);

  // async function handleSubmit(event) {
  //   event.preventDefault();
  //   await submitFormData(formData);
  // }

  function handleUseMatchingActor() {
    setDataFetched(false);
    loadActor(matchingActor.actorId);
    setHeaderText(Translate.get("EditCompany"));
    setSubmitText(getUpdateActorSubmitText(isEditOnly, true, actorId));
    setMatchingActor(null);
  }

  function handleActorNameAutocompleteChange(actor) {
    if (actor && actor.actorId > 0) {
      setDataFetched(false);
      loadActor(actor.actorId);
      setHeaderText(Translate.get("EditCompany"));
      setSubmitText(getUpdateActorSubmitText(isEditOnly, true, actorId));
    } else {
      setFormData({
        ...formData,
        actorName: actor?.actorName,
      });

      // License issuers have a separate dialog to manage addresses, although they can ALSO do it here.
      // If the current user is a license issuer, and the company name has changed, then we will ask if
      // they want to update address names too. Finally, if the current user is NOT a license issuer,
      // then we will update address names without asking.
      const hasAddressesWithOtherName = allAddresses
        .filter((adr) => !isNullUndefinedOrEmpty(adr.actorAddressStreet1))
        .some((adr) => adr.actorAddressStreet1.trim() !== actor?.actorName.trim());
      setShowUpdateAddressNameQuestionOnSubmit(hasExtendedAddressManagement && hasAddressesWithOtherName);

      setIsBlankForm(false);
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }

    if (onClose) {
      onClose();
    } else {
      closeAndReload();
    }
  }

  function updateInvoiceAddress(updatedInvoiceAddress) {
    setFormData({
      ...formData,
      invoiceAddress: updatedInvoiceAddress,
      postalAddress: isPostalSameAsInvoiceAddressChecked
        ? {
            ...updatedInvoiceAddress,
            actorAddressId: formData.postalAddress?.actorAddressId,
          }
        : formData.postalAddress,
    });
  }

  function handleCheckSameAddress(event) {
    const isChecked = event.target.checked;
    if (isChecked) {
      setFormData({
        ...formData,
        postalAddress: {
          ...formData.invoiceAddress,
          actorAddressId: formData.postalAddress?.actorAddressId,
        },
      });
    }
    setIsPostalSameAsInvoiceAddressChecked(isChecked);
  }

  const deleteImage = async (actorId) => {
    const response = await api.fetch(`${import.meta.env.VITE_IMAGE_SERVICE_URL}DeleteActorImageDatabase`, {
      actorId: actorId,
    });
    if (response?.isSuccessful !== false) {
      delete formData.profilePictureUrl;
      setFormData({ ...formData });
    }
  };

  // Using this to avoid validating the potentially empty
  // name value when opening an empty form the first time
  const isActorNameBlank = !("actorName" in formData) || typeof formData?.actorName === typeof null;

  function countryIsRequired(actorAddress) {
    if (!hasAccess(accessKeys.isMASystem) || !actorAddress) return false;

    return Boolean(
      actorAddress.actorAddressStreet2?.trim() ||
      actorAddress.actorAddressZipCode?.trim() ||
      actorAddress.actorAddressCity?.trim(),
    );
  }

  return (
    <>
      {!dataFetched && (
        <>
          <Preloader />
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent className="stdDialogContent"></DialogContent>
        </>
      )}
      {dataFetched && (
        <>
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent sx={{ width: { md: "900px" } }} dividers={true} className="stdDialogContent dialogOverlay">
            <form className="twoColForm">
              <div className="formAvatar">
                <div className="smallHeader">{Translate.get("Logotype")}</div>
                <EPortAvatar
                  avatarWidth={160}
                  avatarHeight={"auto"}
                  variant="rounded"
                  //avatarWidth={160}
                  //avatarHeight={160}
                  fontSize={72}
                  displayName={formData.actorName ? formData.actorName : "?"}
                  pictureUrl={formData.profilePictureUrl + ""}
                  onImageUploaded={(resp) => setImageResponse(resp)}
                  onDeleteImage={() => deleteImage(actorId)}
                  imageType={"company"}
                />
              </div>
              <div className="stdForm">
                <div className="smallHeader">{Translate.get("CompanyInfo")}</div>
                <div>
                  <AutocompleteSearchField
                    placeholder={Translate.get("Name")}
                    label={Translate.get("Name")}
                    value={{ actorName: formData.actorName ?? "" }}
                    onValueChange={handleActorNameAutocompleteChange}
                    getOptionLabel={(option) => option?.actorName ?? ""}
                    renderOption={(option) => (
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        orgNo={option?.orgNo}
                        email={option?.email}
                        companies={option?.companies}
                      />
                    )}
                    keyPropName="actorId"
                    search={
                      !formData || !formData.actorId || formData.actorId <= 0 ? actorNameAutocompleteSearch : null
                    }
                    createNewValue={(text) => ({
                      actorName: text,
                    })}
                    textFieldProps={{
                      required: true,
                      error: !isActorNameBlank && !!errors["actorName"],
                      helperText: !isActorNameBlank && errors["actorName"],
                    }}
                    hideOptionsFilter={formData.actorId > 0 && ((option) => option.actorId === formData.actorId)}
                  />
                  <TextField
                    fullWidth
                    value={formData.orgNo ?? ""}
                    onChange={(event) => {
                      setFormData({ ...formData, orgNo: event.target.value });
                      setIsBlankForm(false);
                      debouncedExactMatchActorSearch(event.target.value, ["OrgNo"]);
                    }}
                    label={Translate.get("OrgNo")}
                    error={errors["orgNo"]}
                    helperText={errors["orgNo"]}
                  />
                </div>
                <div className="smallHeader">{Translate.get("ContactInformation")}</div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.actorEmail ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        actorEmail: event.target.value,
                      });
                      setIsBlankForm(false);
                      debouncedExactMatchActorSearch(event.target.value, ["ActorEmail"]);
                    }}
                    label={Translate.get("Email")}
                    error={errors["actorEmail"] && true}
                    helperText={errors["actorEmail"]}
                  />
                  <TextField
                    fullWidth
                    value={formData.businessPhone?.actorPhoneNumber ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        businessPhone: {
                          actorPhoneNumber: event.target.value,
                          actorId: formData.actorId,
                        },
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("PhoneNbr")}
                    error={!!errors["actorPhoneBusiness"]}
                    helperText={errors["actorPhoneBusiness"]}
                  />
                </div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.webSite ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        webSite: event.target.value,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("WebPage")}
                    error={!!errors["webpage"]}
                    helperText={errors["webpage"]}
                  />
                  <TextField
                    fullWidth
                    value={formData.mobilePhone?.actorPhoneNumber ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        mobilePhone: {
                          actorPhoneNumber: event.target.value,
                          actorId: formData.actorId,
                        },
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("MobilePhone")}
                    error={!!errors["actorPhoneMobile"]}
                    helperText={errors["actorPhoneMobile"]}
                  />
                </div>
                <div className="smallHeader">{Translate.getActorAddressType(1)}</div>
                <div>
                  <TextField
                    
                    fullWidth
                    value={formData.invoiceAddress?.actorAddressStreet2 ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.invoiceAddress,
                        actorAddressStreet2: event.target.value,
                        actorId: formData.actorId,
                      };
                      updateInvoiceAddress(updatedAddress);
                      setIsBlankForm(false);
                    }}
                    error={!!errors["invoiceAddressStreet2"]}
                    helperText={errors["invoiceAddressStreet2"]}
                    label={Translate.get("StreetAddress")}
                  />
                  <TextField
                    fullWidth
                    value={formData.invoiceAddress?.actorAddressZipCode ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.invoiceAddress,
                        actorAddressZipCode: event.target.value,
                        actorId: formData.actorId,
                      };
                      updateInvoiceAddress(updatedAddress);
                      setIsBlankForm(false);
                    }}
                    error={!!errors["invoiceAddressZipCode"]}
                    helperText={errors["invoiceAddressZipCode"]}
                    label={Translate.get("ZipCode")}
                  />
                </div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.invoiceAddress?.actorAddressCity ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.invoiceAddress,
                        actorAddressCity: event.target.value,
                        actorId: formData.actorId,
                      };
                      updateInvoiceAddress(updatedAddress);
                      setIsBlankForm(false);
                    }}
                    error={!!errors["invoiceAddressCity"]}
                    helperText={errors["invoiceAddressCity"]}
                    label={Translate.get("City")}
                  />
                  <TextField
                    fullWidth
                    value={formData.invoiceAddress?.actorAddressCountry ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.invoiceAddress,
                        actorAddressCountry: event.target.value,
                        actorId: formData.actorId,
                      };
                      updateInvoiceAddress(updatedAddress);
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("Country")}
                    required={countryIsRequired(formData.invoiceAddress)}
                    error={!!errors["invoiceAddressCountry"]}
                  />
                </div>
                <Box display="flex" flexDirection="row" justifyContent="space-between">
                  <div className="smallHeader">{Translate.getActorAddressType(3)}</div>
                  <Box>
                    <FormControlLabel
                      classes={{ label: "smallHeader camelCase" }}
                      control={
                        <Checkbox
                          sx={{ marginTop: "8px" }}
                          onChange={(event) => handleCheckSameAddress(event)}
                          checked={isPostalSameAsInvoiceAddressChecked}
                        />
                      }
                      labelPlacement="start"
                      label={Translate.get("SameAsInvoiceAddress")}
                    />
                  </Box>
                </Box>
                <div>
                  <TextField
                    fullWidth
                    disabled={isPostalSameAsInvoiceAddressChecked}
                    value={formData.postalAddress?.actorAddressStreet2 ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressStreet2: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      setIsBlankForm(false);
                    }}
                    error={!!errors["postalAddressStreet2"]}
                    helperText={errors["postalAddressStreet2"]}
                    label={Translate.get("StreetAddress")}
                  />

                  <TextField
                    fullWidth
                    disabled={isPostalSameAsInvoiceAddressChecked}
                    value={formData.postalAddress?.actorAddressZipCode ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressZipCode: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      setIsBlankForm(false);
                    }}
                    error={!!errors["postalAddressZipCode"]}
                    helperText={errors["postalAddressZipCode"]}
                    label={Translate.get("ZipCode")}
                  />
                </div>
                <div>
                  <TextField
                    fullWidth
                    disabled={isPostalSameAsInvoiceAddressChecked}
                    value={formData.postalAddress?.actorAddressCity ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressCity: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      setIsBlankForm(false);
                    }}
                    error={!!errors["postalAddressCity"]}
                    helperText={errors["postalAddressCity"]}
                    label={Translate.get("City")}
                  />
                  <TextField
                    fullWidth
                    disabled={isPostalSameAsInvoiceAddressChecked}
                    value={formData.postalAddress?.actorAddressCountry ?? ""}
                    onChange={(event) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressCountry: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("Country")}
                    required={countryIsRequired(formData.postalAddress)}
                    error={!!errors["postalAddressCountry"]}
                  />
                </div>
                <div>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={formData.observationNote ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        observationNote: event.target.value,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("ObservationNote")}
                    inputProps={{ maxLength: 255 }}
                  />
                </div>
                <div style={{ width: "100%", padding: 0 }}>
                  <Accordion style={{ width: "100%", left: "-1.5rem", margin: 0 }}>
                    <AccordionSummary
                      className="smallHeader"
                      sx={{ paddingLeft: "1.3rem", width: "105%" }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      {Translate.get("BillingInformation")}
                    </AccordionSummary>
                    <AccordionDetails sx={{ paddingLeft: "1.4rem", width: "105%" }}>
                      <div class="stdForm">
                        {hasAccess(accessKeys.isMASystem) && (
                          <div>
                            <FormControl fullWidth>
                              <InputLabel id="languagecode-label">{Translate.get("Language")}</InputLabel>
                              <Select
                                label={Translate.get("Language")}
                                labelId="languagecode-label"
                                value={formData.languageCode ?? ""}
                                onChange={(event) => {
                                  setFormData({
                                    ...formData,
                                    languageCode: event.target.value,
                                  });
                                  setIsBlankForm(false);
                                }}
                              >
                                {languageOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {Translate.get(option.label)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}

                        {hasAccess(accessKeys.isMASystem) && (
                          <div>
                            <FormControl fullWidth>
                              <InputLabel id="currency-label">{Translate.get("CurrencyLabel")}</InputLabel>
                              <Select
                                label={Translate.get("CurrencyLabel")}
                                labelId="currency-label"
                                value={formData.defaultCurrency ?? ""}
                                onChange={(event) => {
                                  setFormData({
                                    ...formData,
                                    defaultCurrency: event.target.value,
                                  });
                                  setIsBlankForm(false);
                                }}
                              >
                                {currencies.map((currency) => (
                                  <MenuItem value={currency.code}>{currency.code}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}
                        <div>
                          {hasAccess(accessKeys.isMASystem) && (
                            <div>
                              <FormControl fullWidth>
                                <InputLabel id="vat-type-label">{Translate.get("VatType")}</InputLabel>
                                <Select
                                  label={Translate.get("VatType")}
                                  labelId="vat-type-label"
                                  value={formData.vatType ?? ""}
                                  onChange={(event) => {
                                    setFormData({
                                      ...formData,
                                      vatType: event.target.value,
                                    });
                                    setIsBlankForm(false);
                                  }}
                                >
                                  {[1, 2, 3].map((vatType) => (
                                    <MenuItem value={vatType}>{Translate.getVatType(vatType)}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </div>
                          )}
                          <TextField
                            fullWidth
                            value={formData.vatNr}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                vatNr: event.target.value,
                              });
                              setIsBlankForm(false);
                            }}
                            label={Translate.get("VatNo")}
                            inputProps={{ maxLength: 14, spellCheck: false }}
                            error={formData.vatNr && !validateVatNumber(formData.vatNr)}
                            helperText={
                              !formData.vatNr || validateVatNumber(formData.vatNr) ? "" : Translate.get("InvalidFormat")
                            }
                          />
                        </div>
                        {hasAccess(accessKeys.isMASystem) && (
                          <div>
                            <FormControl fullWidth>
                              <InputLabel id="payment-agreement-label">{Translate.get("PaymentTerm")}</InputLabel>
                              <Select
                                label={Translate.get("PaymentTerm")}
                                labelId="payment-agreement-label"
                                value={formData.paymentAgreement ?? "defaultPaymentAgreement"}
                                onChange={(event) => {
                                  setFormData({
                                    ...formData,
                                    paymentAgreement: event.target.value,
                                  });
                                  setIsBlankForm(false);
                                }}
                              >
                                {paymentAgreements.map((pa) => (
                                  <MenuItem
                                    value={pa.ePortCodeId}
                                    disabled={pa.isPrepayPaymentAgreement && formData.isAutoOrderAllowed}
                                  >
                                    {pa.description}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}
                        <div>
                          <TextField
                            fullWidth
                            value={formData.defaultCustomerOrderNo ?? ""}
                            onChange={(event) => {
                              setFormData({
                                ...formData,
                                defaultCustomerOrderNo: event.target.value,
                              });
                              setIsBlankForm(false);
                            }}
                            label={Translate.get("InvoiceInfoCustomerOrderNo")}
                            inputProps={{ maxLength: 30 }}
                          />
                          {hasAccess(accessKeys.isMASystem) && (
                            <FormControlLabel
                              fullWidth
                              label={Translate.get("DefaultCustomerOrderNoRequired")}
                              control={
                                <Checkbox
                                  checked={formData.defaultCustomerOrderNoRequired}
                                  onChange={(event) =>
                                    setFormData({
                                      ...formData,
                                      defaultCustomerOrderNoRequired: event.target.checked,
                                    })
                                  }
                                />
                              }
                              labelPlacement="start"
                            />
                          )}
                        </div>
                        <TextField
                          fullWidth
                          value={formData.externalActorNo ?? ""}
                          onChange={(event) => {
                            setFormData({
                              ...formData,
                              externalActorNo: event.target.value,
                            });
                            setIsBlankForm(false);
                          }}
                          label={Translate.get("ExternalCustomerNo")}
                          inputProps={{ maxLength: 25 }}
                        />
                        {hasAccess(accessKeys.isMASystem) && (
                          <div>
                            <FormControl fullWidth>
                              <InputLabel id="contact-select-label">
                                {Translate.get("DefaultSeparateInvoiceRefActorId")}
                              </InputLabel>
                              <Select
                                disabled={!contacts || contacts.length === 0}
                                labelId="contact-select-label"
                                id="contact-select"
                                value={
                                  !!contacts && contacts.length > 0 ? (defaultSeparateInvoiceRefActorId ?? "") : ""
                                }
                                label={Translate.get("DefaultSeparateInvoiceRefActorId")}
                                onChange={(event) => setDefaultSeparateInvoiceRefActorId(event.target.value)}
                                endAdornment={
                                  !!contacts &&
                                  contacts.length > 0 &&
                                  !!defaultSeparateInvoiceRefActorId &&
                                  !!contacts.find((c) => c.actorId == defaultSeparateInvoiceRefActorId) && (
                                    <InputAdornment sx={{ marginRight: "10px" }} position="end">
                                      <IconButton onClick={() => setDefaultSeparateInvoiceRefActorId(null)}>
                                        <Clear></Clear>
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }
                              >
                                {contacts &&
                                  contacts.map((contact) => (
                                    <MenuItem key={contact.actorId} value={contact.actorId}>
                                      {contact.actorName}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </div>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
                {hasAccess(accessKeys.isMASystem) && (
                  <Tooltip
                    placement="top-end"
                    title={`${Translate.get("CompanyIsActive")} : ${
                      !formData.isInactive ? Translate.get("Yes") : Translate.get("No")
                    }`}
                  >
                    <Stack justifyContent="space-between" direction="row" disableGutters>
                      <Container
                        disableGutters
                        sx={{
                          paddingTop: ".5rem",
                          //paddingLeft:".7rem"
                        }}
                        onClick={() =>
                          setFormData({
                            ...formData,
                            isInactive: !formData.isInactive,
                          })
                        }
                      >
                        {Translate.get("CompanyIsActive")}
                      </Container>
                      <Switch
                        checked={!formData.isInactive}
                        name="companyIsActive"
                        onChange={(evt) =>
                          setFormData({
                            ...formData,
                            isInactive: !evt.target.checked,
                          })
                        }
                      />
                    </Stack>
                  </Tooltip>
                )}
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel}>{Translate.get("Cancel")}</Button>
            <Button
              className="stdLightLoader"
              loading={isSubmitLoading}
              disabled={submitDisabled}
              onClick={() => handleSubmitClick(formData)}
            >
              {submitText}
            </Button>
          </DialogActions>
          {/* YesOrNoDialog for asking if we should use an existing matching actor instead */}
          <YesOrNoDialog
            open={!!matchingActor && matchingActor.actorId !== formData.actorId}
            title={Translate.get("UseMatchingCompanyTitle")}
            content={
              !!matchingActor ? (
                <>
                  {Translate.get("UseMatchingCompanyQuestion")}
                  <Box marginTop="24px">
                    <ActorAvatar
                      name={matchingActor.actorName}
                      email={matchingActor.email}
                      extraFields={[
                        matchingActor.orgNo,
                        matchingActor.externalActorNo,
                        matchingActor.parentActors
                          ? [...matchingActor.parentActors.map((parent) => parent.parentActor.actorName)]
                          : [],
                      ]}
                      profilePictureUrl={matchingActor.profilePictureUrl}
                      vertical
                    />
                  </Box>
                </>
              ) : (
                ""
              )
            }
            onNo={() => setMatchingActor(null)}
            onYes={handleUseMatchingActor}
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
          />
          <YesOrNoDialog
            open={showUpdateAddressNameQuestion}
            title={Translate.get("UpdateAddressName")}
            content={`${Translate.get("UpdateAddressNameContent")}\n\n${getAddressesWithDifferentNames(
              allAddresses,
              formData?.actorName,
            )}`}
            onNo={async () => await submitFormData(formData, false)}
            onYes={async () => await submitFormData(formData, true)}
            onCancel={() => setShowUpdateAddressNameQuestion(false)}
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
            showCancelButton
          />
          {/* Allt innehåll */}
        </>
      )}
    </>
  );
};
export default CompanyCrud;
